// Client logins of a portal: invite, edit clients, enable/disable,
// resend invite (copyable link), delete.

import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Block, CheckCircle, Delete, Edit, PersonAdd, Send } from '@mui/icons-material'

import {
    useCreatePortalUserMutation,
    useDeletePortalUserMutation,
    useListPortalUsersQuery,
    useResendPortalInviteMutation,
    useUpdatePortalUserMutation,
} from '@/apis/v2Api'
import { isEmail, type Portal, type PortalUser } from '@/apis/portalTypes'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { useProjectId } from '@/hooks/useReportArgs'
import { ReportStatus, SectionCard, fmtDateTime } from '@/modules/reports/common'

import { ApiErrorAlert } from './ApiErrorAlert'
import { SubTenantMultiPicker } from './SubTenantPicker'

interface InviteInfo {
    email: string
    invite_url: string
    emailed: boolean
    unregistered?: string[]
}

function InviteLinkDialog({ info, onClose }: { info: InviteInfo; onClose: () => void }) {
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Invitation for {info.email}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2 }}>
                {info.emailed ? (
                    <Alert severity="success">The invitation email was sent.</Alert>
                ) : (
                    <Alert severity="warning">
                        The email was not sent (email isn't configured). Share the link below with
                        your client yourself.
                    </Alert>
                )}
                {!!info.unregistered?.length && (
                    <Alert severity="warning">
                        Not registered as sub-tenants yet: {info.unregistered.join(', ')}. The login
                        works, but its reports stay empty until events arrive for these ids.
                    </Alert>
                )}
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Invite link (valid 7 days, single use)
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: 13,
                            wordBreak: 'break-all',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>{info.invite_url}</Box>
                        <CopyButton text={info.invite_url} />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    )
}

function UserDialog({
    portal,
    user,
    onClose,
    onInvited,
}: {
    portal: Portal
    user: PortalUser | null
    onClose: () => void
    onInvited: (info: InviteInfo) => void
}) {
    const projectId = useProjectId()
    const [email, setEmail] = useState(user?.email ?? '')
    const [name, setName] = useState(user?.name ?? '')
    const [subTenants, setSubTenants] = useState<string[]>(user?.sub_tenants ?? [])
    const [create, createState] = useCreatePortalUserMutation()
    const [update, updateState] = useUpdatePortalUserMutation()
    const valid = (user || isEmail(email.trim().toLowerCase())) && subTenants.length > 0
    const busy = createState.isLoading || updateState.isLoading

    const save = async () => {
        try {
            if (user) {
                await update({
                    projectId,
                    portalId: portal.id,
                    userId: user.id,
                    name: name.trim(),
                    sub_tenants: subTenants,
                }).unwrap()
                onClose()
            } else {
                const r = await create({
                    projectId,
                    portalId: portal.id,
                    email: email.trim().toLowerCase(),
                    name: name.trim() || undefined,
                    sub_tenants: subTenants,
                }).unwrap()
                onInvited({
                    email: r.email,
                    invite_url: r.invite_url,
                    emailed: r.emailed,
                    unregistered: r.unregistered_sub_tenants,
                })
            }
        } catch {
            // shown below
        }
    }

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{user ? `Edit ${user.email}` : 'Invite a client login'}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2 }}>
                <ApiErrorAlert error={createState.error ?? updateState.error} />
                {!user && (
                    <TextField
                        size="small"
                        label="Email"
                        type="email"
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                )}
                <TextField
                    size="small"
                    label="Name (optional)"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    sx={user ? { mt: 1 } : undefined}
                />
                <SubTenantMultiPicker
                    projectId={projectId}
                    value={subTenants}
                    onChange={setSubTenants}
                />
                <Typography variant="caption" color="text.secondary">
                    The login only ever sees these clients' data. With more than one, it gets a
                    client switcher.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!valid || busy}
                    onClick={() => {
                        void save()
                    }}
                >
                    {user ? 'Save' : 'Send invite'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
    active: 'success',
    invited: 'warning',
    disabled: 'default',
}

export function PortalUsersCard({ portal }: { portal: Portal }) {
    const projectId = useProjectId()
    const args = { projectId, portalId: portal.id }
    const { data = [], isLoading, error } = useListPortalUsersQuery(args)
    const [update, updateState] = useUpdatePortalUserMutation()
    const [resend, resendState] = useResendPortalInviteMutation()
    const [remove, removeState] = useDeletePortalUserMutation()
    const [editing, setEditing] = useState<PortalUser | 'new' | null>(null)
    const [invite, setInvite] = useState<InviteInfo | null>(null)
    const [toDelete, setToDelete] = useState<PortalUser | null>(null)

    const resendInvite = async (u: PortalUser) => {
        try {
            const r = await resend({ ...args, userId: u.id }).unwrap()
            setInvite({ email: u.email, ...r })
        } catch {
            // shown below
        }
    }

    return (
        <SectionCard
            title="Client logins"
            action={
                <Button startIcon={<PersonAdd />} onClick={() => setEditing('new')}>
                    Invite
                </Button>
            }
        >
            <ApiErrorAlert
                error={updateState.error ?? resendState.error ?? removeState.error}
                sx={{ mb: 2 }}
            />
            {error ? (
                <ApiErrorAlert error={error} />
            ) : (
                <ReportStatus
                    isLoading={isLoading}
                    isEmpty={data.length === 0}
                    emptyTitle="No client logins yet"
                    emptyDescription="Invite your client: they choose a password and see only their own sub-tenant's reports."
                >
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Login</TableCell>
                                    <TableCell>Clients</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Last sign-in</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map(u => (
                                    <TableRow key={u.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {u.email}
                                            </Typography>
                                            {u.name && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {u.name}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 260 }}>
                                            {u.sub_tenants.map(st => (
                                                <Chip
                                                    key={st}
                                                    size="small"
                                                    label={st}
                                                    sx={{
                                                        mr: 0.5,
                                                        mb: 0.5,
                                                        fontFamily: 'monospace',
                                                    }}
                                                />
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={u.status}
                                                color={STATUS_COLOR[u.status] ?? 'default'}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {u.last_login_at ? fmtDateTime(u.last_login_at) : '—'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Edit name / clients">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setEditing(u)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {u.status !== 'disabled' && (
                                                <Tooltip
                                                    title={
                                                        u.status === 'invited'
                                                            ? 'Resend invite / get link'
                                                            : 'Send a new invite link'
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Send invite link"
                                                            disabled={resendState.isLoading}
                                                            onClick={() => {
                                                                void resendInvite(u)
                                                            }}
                                                        >
                                                            <Send fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip
                                                title={
                                                    u.status === 'disabled' ? 'Enable' : 'Disable'
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        void update({
                                                            ...args,
                                                            userId: u.id,
                                                            status:
                                                                u.status === 'disabled'
                                                                    ? 'active'
                                                                    : 'disabled',
                                                        })
                                                    }}
                                                >
                                                    {u.status === 'disabled' ? (
                                                        <CheckCircle fontSize="small" />
                                                    ) : (
                                                        <Block fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setToDelete(u)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </ReportStatus>
            )}

            {editing && (
                <UserDialog
                    portal={portal}
                    user={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onInvited={info => {
                        setEditing(null)
                        setInvite(info)
                    }}
                />
            )}
            {invite && <InviteLinkDialog info={invite} onClose={() => setInvite(null)} />}
            <ConfirmDialog
                open={!!toDelete}
                title="Delete client login?"
                message={`${toDelete?.email ?? ''} loses access to this portal immediately.`}
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void remove({ ...args, userId: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </SectionCard>
    )
}
